/**
 * Простая система групп слоев
 * Работает через data-атрибуты и визуальное отображение
 */

export interface LayerGroupData {
    id: string;
    name: string;
    layerIndices: number[];
    isCollapsed: boolean;
}

class SimpleLayerGroups {
    // Changed from Map to Array to align with the provided `createGroup` and `addLayerToGroup` logic
    private groups: LayerGroupData[] = [];
    private readonly STORAGE_KEY = 'kleki-layer-groups';

    constructor() {
        this.load();
    }

    // Helper to generate unique IDs
    private generateId(): string {
        return `group_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    /**
     * Create a new group with the given name and optionally add a layer
     */
    createGroup(name: string, layerIndex?: number): string {
        const id = this.generateId();
        const group: LayerGroupData = { // Changed TLayerGroup to LayerGroupData
            id,
            name,
            layerIndices: layerIndex !== undefined ? [layerIndex] : [],
            isCollapsed: false,
        };
        this.groups.push(group);
        this.save();
        return id;
    }

    /**
     * Add a layer to an existing group
     */
    addLayerToGroup(groupId: string, layerIndex: number): void {
        const group = this.groups.find(g => g.id === groupId);
        if (!group) {
            throw new Error(`Group ${groupId} not found`);
        }

        // Remove layer from any other groups first
        this.groups.forEach(g => {
            g.layerIndices = g.layerIndices.filter(idx => idx !== layerIndex);
        });

        // Add to the specified group if not already there
        if (!group.layerIndices.includes(layerIndex)) {
            group.layerIndices.push(layerIndex);
        }

        this.save();
    }

    renameGroup(groupId: string, newName: string): void {
        const group = this.groups.find(g => g.id === groupId);
        if (group) {
            group.name = newName;
            this.save();
        }
    }

    toggleCollapse(groupId: string): boolean {
        // Changed from Map.get to Array.find
        const group = this.groups.find(g => g.id === groupId);
        if (group) {
            group.isCollapsed = !group.isCollapsed;
            this.save();
            return group.isCollapsed;
        }
        return false;
    }

    getGroupForLayer(layerIndex: number): LayerGroupData | null {
        for (const group of this.groups) {
            if (group.layerIndices.includes(layerIndex)) {
                return group;
            }
        }
        return null;
    }

    getAllGroups(): LayerGroupData[] {
        return this.groups;
    }

    deleteGroup(groupId: string): void {
        this.groups = this.groups.filter(g => g.id !== groupId);
        this.save();
    }

    private save(): void {
        try {
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.groups));
        } catch (e) {
            console.warn('Failed to save groups:', e);
        }
    }

    private load(): void {
        try {
            const data = localStorage.getItem(this.STORAGE_KEY);
            if (data) {
                this.groups = JSON.parse(data);
            }
        } catch (e) {
            console.warn('Failed to load groups:', e);
        }
    }

    clear(): void {
        this.groups = [];
        this.save();
    }
}

// Export singleton
export const simpleLayerGroups = new SimpleLayerGroups();
